import {AsyncPipe} from '@angular/common';
import {AfterViewInit, Component, ElementRef, forwardRef, HostListener, Input, OnInit, ViewChild,} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {toggleMark} from 'prosemirror-commands';
import {exampleSetup} from 'prosemirror-example-setup';
import {defaultMarkdownParser, defaultMarkdownSerializer, schema} from 'prosemirror-markdown';
import {liftListItem, wrapInList} from 'prosemirror-schema-list';
import {EditorState, Selection, TextSelection} from 'prosemirror-state';
import {EditorView} from 'prosemirror-view';

import {fromEvent, mapTo, merge, Observable, Subject, tap} from 'rxjs';
import {filter} from 'rxjs/operators';

class ProseMirrorView {
    public editorView: EditorView;
    public focus$: Observable<boolean>;
    public cursorPos$: Subject<number>;
    public content$: Subject<string>;
    public currentMarks$: Subject<string[]>;

    constructor(target, content) {
        this.cursorPos$ = new Subject();
        this.content$ = new Subject<string>();
        this.currentMarks$ = new Subject<string[]>();

        this.editorView = new EditorView(target, {
            state: EditorState.create({
                doc: defaultMarkdownParser.parse(content || ''),
                plugins: exampleSetup({
                    schema: schema,
                    menuBar: false,
                }),
            }),
            dispatchTransaction: (tr) => {
                this.cursorPos$.next(tr.selection.ranges[0].$from.pos);
                const state = this.editorView.state.apply(tr);
                this.editorView.updateState(state);
                this.content$.next(this.content);

                this.currentMarks$.next(this.selectionMarks(state));
            },
        });

        const focus = fromEvent(this.editorView.dom, 'focus');
        this.focus$ = focus.pipe(mapTo(true));
    }

    get content() {
        return defaultMarkdownSerializer.serialize(this.editorView.state.doc);
    }

    setContent(content: string) {
        const newState = EditorState.create({
            doc: defaultMarkdownParser.parse(content || ''),
            plugins: exampleSetup({
                schema: schema,
                menuBar: false,
            }),
        });
        this.editorView.updateState(newState);
    }

    focus() {
        this.editorView.focus();
    }

    destroy() {
        this.editorView.destroy();
    }

    private getNodeBeforeCursor(selection: Selection) {
        return selection instanceof TextSelection ? selection.$cursor!.nodeBefore : undefined;
    }

    private selectionMarks(state: EditorState) {
        const { $from, $to } = state.selection;
        const availableMarks = schema.marks;
        const availableMarksLength = Object.keys(availableMarks).length;
        const currentMarks = [];

        // Just get the first list type if available
        for (let i = $from.depth; i > 0; i--) {
            if ($from.node(i).type.name === 'list_item') {
                currentMarks.push($from.node(i - 1).type.name);
                break;
            }
        }

        if ($from.pos === $to.pos) {
            const nodeBefore = this.getNodeBeforeCursor(state.selection);
            if (nodeBefore) {
                currentMarks.push(...nodeBefore.marks.map((mark) => mark.type.name));
            }

            return currentMarks;
        }

        state.doc.nodesBetween($from.pos, $to.pos, (node) => {
            if (availableMarksLength === currentMarks.length) {
                return;
            }

            if (node.marks.length !== 0) {
                for (let i = 0; i < node.marks.length; i += 1) {
                    const nodeType = node.marks[i].type.name;
                    if (currentMarks.indexOf(nodeType) === -1) {
                        currentMarks.push(nodeType);
                    }
                }
            }
        });

        return currentMarks;
    }
}

@Component({
    selector: 'frog-markdown-textarea',
    templateUrl: './markdown-textarea.component.html',
    styleUrls: ['./markdown-textarea.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => MarkdownTextareaComponent),
        },
    ],
    imports: [MatButton, MatIcon, AsyncPipe],
})
export class MarkdownTextareaComponent implements OnInit, AfterViewInit, ControlValueAccessor {
    @ViewChild('editor') editor: ElementRef;
    @ViewChild('menu') menu: ElementRef;
    @ViewChild('container') container: ElementRef;

    @Input() appearance: string = 'filled';

    public view: ProseMirrorView;
    public active$: Observable<boolean>;
    public content$: Observable<any>;
    public marks$: Observable<string[]>;
    public disabled: boolean;
    public isBold: boolean;
    public isItalic: boolean;
    public isBulletedList: boolean;
    public isOrderedList: boolean;

    private _active: Subject<boolean>;
    private _content: string;
    private _isActive: boolean;

    private onChange = (string) => {};

    constructor() {
        this._active = new Subject<boolean>();
    }

    ngOnInit(): void {}

    ngAfterViewInit() {
        this.view = new ProseMirrorView(this.editor.nativeElement, this._content);

        this.active$ = merge(this.view.focus$, this._active).pipe(
            tap((value) => (this._isActive = value)),
        );

        this.content$ = this.view.content$.pipe(
            filter((value) => value !== this._content),
            tap((content) => {
                this._content = content;
                this.onChange(content);
            }),
        );

        this.marks$ = this.view.currentMarks$.pipe(
            tap((marks) => {
                this.isBold = marks.includes(schema.marks.strong.name);
                this.isItalic = marks.includes(schema.marks.em.name);
                this.isBulletedList = marks.includes(schema.nodes.bullet_list.name);
                this.isOrderedList = marks.includes(schema.nodes.ordered_list.name);
            }),
        );
    }

    writeValue(value: string) {
        this._content = value;
        if (this.view) {
            this.view.setContent(value);
        }
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {}

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    @HostListener('window:mousedown', ['$event.target'])
    mouseUpHandler(target: HTMLElement) {
        if (!this._isActive) {
            return;
        }

        if (!this.container.nativeElement.contains(target)) {
            this.onChange(this.view.content);
            this._active.next(false);
        }
    }

    bold(event: MouseEvent) {
        this.command(event, toggleMark(schema.marks.strong));
    }

    italic(event: MouseEvent) {
        this.command(event, toggleMark(schema.marks.em));
    }

    bulletList(event: MouseEvent) {
        if (this.isBulletedList) {
            this.command(event, liftListItem(schema.nodes.bullet_list));
        } else {
            this.command(event, wrapInList(schema.nodes.bullet_list));
        }
    }

    orderedList(event: MouseEvent) {
        this.command(event, wrapInList(schema.nodes.ordered_list));
    }

    command(event: MouseEvent, command: any) {
        event.preventDefault();
        event.stopPropagation();
        this.view.focus();
        command(this.view.editorView.state, this.view.editorView.dispatch, this.view.editorView);
    }
}
